import {gql} from '@apollo/client';
import {PartnerPenaltyFiltersTypeInput, WarehousePenaltyFiltersTypeInput} from './types';

/**
 * 发起申诉 / 放弃申诉。
 * 发起申诉时带申诉理由与证据图；放弃申诉只改状态，后端忽略这两个字段。
 * 后端从登录上下文取 dspId 校验罚单归属，只允许从「待提交」流转，并复核 48h 申诉窗口——
 * 前端置灰与二次确认仅为体验优化，是否真的还能操作以后端判定为准。
 */
export const APPEAL_PARTNER_PENALTY = gql`
    mutation AppealPartnerPenalty(
        $id: Long!,
        $action: Int!,
        $appealReason: String,
        $evidenceObjectKeys: [String!]
    ) {
        appealPartnerPenalty(
            id: $id,
            action: $action,
            appealReason: $appealReason,
            evidenceObjectKeys: $evidenceObjectKeys
        )
    }
`;

export interface AppealPartnerPenaltyResponse {
    appealPartnerPenalty: boolean;
}

export interface AppealPartnerPenaltyVariables {
    id: number;
    /** 目标状态值，见 PENALTY_APPEAL_ACTION */
    action: number;
    /** 发起申诉时必填，最长 256；放弃申诉不传 */
    appealReason?: string | null;
    /** 证据图 objectKey（非 URL），最多 9 张；无图时不传 */
    evidenceObjectKeys?: string[] | null;
}

/**
 * 保存申诉草稿：只写申诉材料，罚单状态保持「待提交」。
 * 重复保存为整组重写（后端软删旧组 + 批量插入），不会累积历史图片。
 */
export const SAVE_PARTNER_PENALTY_APPEAL_DRAFT = gql`
    mutation SavePartnerPenaltyAppealDraft(
        $id: Long!,
        $appealReason: String!,
        $evidenceObjectKeys: [String!]
    ) {
        savePartnerPenaltyAppealDraft(
            id: $id,
            appealReason: $appealReason,
            evidenceObjectKeys: $evidenceObjectKeys
        )
    }
`;

export interface SavePartnerPenaltyAppealDraftResponse {
    savePartnerPenaltyAppealDraft: boolean;
}

export interface SavePartnerPenaltyAppealDraftVariables {
    id: number;
    appealReason: string;
    evidenceObjectKeys?: string[] | null;
}

/** 导出当前筛选全量（异步生成，去下载中心领取） */
export const GENERATE_PARTNER_PENALTY_REPORT = gql`
    mutation GeneratePartnerPenaltyReport(
        $filters: PartnerPenaltyFiltersTypeInput!,
        $language: String,
        $timezone: String
    ) {
        generatePartnerPenaltyReport(
            filters: $filters,
            language: $language,
            timezone: $timezone
        ) {
            success
            errorCode
            fileName
        }
    }
`;

export interface GeneratePartnerPenaltyReportResponse {
    generatePartnerPenaltyReport: {
        success: boolean;
        errorCode?: string | null;
        fileName?: string | null;
    };
}

export interface GeneratePartnerPenaltyReportVariables {
    filters: PartnerPenaltyFiltersTypeInput;
    language?: string | null;
    timezone?: string | null;
}

/** 仓库侧导出。alias 对齐 DSP 侧响应 key，复用 GeneratePartnerPenaltyReportResponse */
export const GENERATE_WAREHOUSE_PENALTY_REPORT = gql`
    mutation GenerateWarehousePenaltyReport(
        $filters: WarehousePenaltyFiltersTypeInput!,
        $language: String,
        $timezone: String
    ) {
        generatePartnerPenaltyReport: generateWarehousePenaltyReport(
            filters: $filters,
            language: $language,
            timezone: $timezone
        ) {
            success
            errorCode
            fileName
        }
    }
`;

export interface GenerateWarehousePenaltyReportVariables {
    filters: WarehousePenaltyFiltersTypeInput;
    language?: string | null;
    timezone?: string | null;
}
